import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { BaseFormControlledModal } from "../../../../../../common/components/base_form_controlled_modal";
import FormContainer from "../../../../../../common/components/form_container";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import { FormDatePickerInput } from "../../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import { FormMoneyMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_money_masked_input";
import { ProvisaoRecursoLista } from "../../../../domain/models/provisao_recurso_lista";
import { QualidadeProvisaoRecursoForm, QualidadeProvisaoRecursoSchema } from "../../../schemas/ProvisaoRecurso/qualidade_provisao_recurso";
import { useCallback, useEffect } from "react";
import { LogTipoTabela } from "../../../../../../common/utils/log_tabela_tipo";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import PrimaryButton from "../../../../../../common/components/primary_button";
import { isNotNullOrUndefined } from "../../../../../../common/utils/utils";

// ProvisaoRecursoModal 
 
type Props = {
  open: boolean;
  saving: boolean;
  loading: boolean;
  selectedProvisaoRecurso: ProvisaoRecursoLista | undefined, 
  onClose: () => void;
  onSubmit: (e: QualidadeProvisaoRecursoForm) => void;
  onOpenChangeHistory: (id: number, tabelaTipo: LogTipoTabela) => void;
};

export const ProvisaoRecursoModal = ({
  open, saving,loading, selectedProvisaoRecurso,
  onClose, onSubmit,onOpenChangeHistory
}: Props) => {

  const hookForm = useForm<QualidadeProvisaoRecursoForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(QualidadeProvisaoRecursoSchema),
  });

  useEffect(() => {
    if (open) {
        hookForm.reset(); 

        if (selectedProvisaoRecurso) {
            hookForm.setValue("Recurso", selectedProvisaoRecurso.Recurso);
            hookForm.setValue("Observacao", selectedProvisaoRecurso.Observacao ?? null);
            hookForm.setValue("ValorPrevisto", Number(selectedProvisaoRecurso.ValorPrevisto));
            hookForm.setValue("ValorDesembolsado", Number(selectedProvisaoRecurso.ValorDesembolsado));

            if (selectedProvisaoRecurso.DataPrevistaAte)  hookForm.setValue("DataPrevistaAte", new Date(selectedProvisaoRecurso.DataPrevistaAte));
            if (selectedProvisaoRecurso.DataRealizadaAte) hookForm.setValue("DataRealizadaAte", new Date(selectedProvisaoRecurso.DataRealizadaAte));
            if (selectedProvisaoRecurso.DataRealizadaDe) hookForm.setValue("DataRealizadaDe", new Date(selectedProvisaoRecurso.DataRealizadaDe));
            if (selectedProvisaoRecurso.DataPrevistaDe) hookForm.setValue("DataPrevistaDe", new Date(selectedProvisaoRecurso.DataPrevistaDe));
            
        } else {
            hookForm.reset(); 
        }
    }
  }, [hookForm, open, selectedProvisaoRecurso]);
  
  const handleChangeHistory = useCallback(() => {
    if (selectedProvisaoRecurso) {
      onOpenChangeHistory(selectedProvisaoRecurso.IdProvisaoRecurso, LogTipoTabela.PROVISAO_RECURSO);
    }
  }, [selectedProvisaoRecurso, onOpenChangeHistory]);

  return (
  <Dialog
    open={open}
    maxWidth="lg" 
    fullWidth
    PaperProps={{
      component: 'form',
      onSubmit: hookForm.handleSubmit(onSubmit)
    }}
    sx={{ "& .MuiDialog-paper": { maxWidth: "1200px", width: "100%" } }}
  >
    <DialogTitle>
      Provisão de Recursos
    </DialogTitle>

    <DialogContent>
      <FormContainer title={"Cadastrar/Editar Item"} arrangement="fullRow">
        <div className="flex flex-col gap-y-4 w-full pt-2">
          
          <div className="w-full">
            <DefaultFormInput
              name='Recurso'
              label='Recurso'
              required={true}
              reactHookForm={hookForm}
            />
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            <FormDatePickerInput
              name='DataPrevistaDe'
              label='Data de Início'
              required={true}
              reactHookForm={hookForm}
            />
            <FormDatePickerInput
              name='DataPrevistaAte'
              label='Data de Término' 
              reactHookForm={hookForm}
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
               <FormMoneyMaskedInput
                  name='ValorPrevisto'
                  label='Valor Previsto (R$)'
                  required={true}
                  largeNumbers={true}
                  reactHookForm={hookForm}

                />

                <FormMoneyMaskedInput
                name='ValorDesembolsado'
                label='Valor Desembolsado (R$)'
                largeNumbers={true}
                reactHookForm={hookForm}
              />
          </div>
          
          <div className="w-full">
            <DefaultFormInput
              name='Observacao'
              label='Observação'
              rows={4}
              multiline={true}
              reactHookForm={hookForm}
            />
          </div>

        </div>
      </FormContainer>
    </DialogContent>

    <DialogActions>
        <div className="w-full h-full items-center justify-between flex p-4 gap-4">
          <div>
            {
              isNotNullOrUndefined(selectedProvisaoRecurso) &&
              <PrimaryButton
                buttonColor="yellow"
                onClick={handleChangeHistory}>
                <span className="text-xs">
                  Historico de alterações
                </span>
              </PrimaryButton>
            }
          </div>

          <div className="flex flex-row gap-2">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={onClose}>
                Cancelar
              </h1>
            </div>

            <div className="w-[120px]">
              {saving ? (
                <PrimaryButton
                  buttonColor="primary-green">
                  Salvando...
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  type="submit"
                  buttonColor="primary-green">
                  Salvar
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>
      </DialogActions>
  </Dialog>
);
}